import * as moment from 'moment-timezone';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {filter, switchMap, take} from 'rxjs/operators';

export enum DeviceDayStatusEnum {
  active = 'active',
  inactive = 'inactive',
  dayOff = 'dayOff'
}

export interface IDeviceDayInfo {
  start: string;
  finish: string;
  active: boolean;
  // minGuaranteedShows: number;
  // showsCount: number;
}

export interface IDeviceActivity {
  deviceId: number;
  deviceName: string;
  locationName: string;
  timezone: string;
  campaignStart: string;
  campaignFinish: string;
  periods: IDeviceDayInfo[];
}

export interface ICampaignDeviceData {
  campaignStart: string;
  campaignFinish: string;
  devicesActivity: IDeviceActivity[];
}

export interface IDeviceMonthInfo {
  date: string;
  days: {
    dayNumber: number;
    status: DeviceDayStatusEnum
  };
}

export interface IDeviceCalendarData {
  deviceName: string;
  locationName: string;
  activity: IDeviceMonthInfo[];
}

export interface ICalendarDayInfo {
  dayNumber: number;
  date: string;
  isActivityPeriod: boolean;
}

export interface IDeviceCalendarFullData {
  devices: IDeviceCalendarData[];
  calendarHeader: ICalendarDayInfo[];
  isNextDisabled: boolean;
  isPrevDisabled: boolean;
  monthLabel: { month: string, year: number };
  campaignStart: string;
  campaignFinish: string;
}

export class DeviceActivityModel implements ICampaignDeviceData {
  readonly campaignFinish: string;
  readonly campaignStart: string;
  readonly devicesActivity: IDeviceActivity[];
  private targetMonth!: string;
  private targetMonth$: BehaviorSubject<string>;

  constructor(data: ICampaignDeviceData) {
    this.campaignFinish = data.campaignFinish;
    this.campaignStart = data.campaignStart;
    this.devicesActivity = data.devicesActivity;
    this.targetMonth$ = new BehaviorSubject<string>(moment(new Date(this.campaignStart)).format('YYYY-MM'));
    this.initPeriodWorker();
  }

  private get isNextFinished(): boolean {
    const finishDate = moment(new Date(this.campaignFinish)).format('YYYY-MM');
    return moment(finishDate).isSame(this.targetMonth);
  }

  private get isPrevFinished(): boolean {
    const startDate = moment(new Date(this.campaignStart)).format('YYYY-MM');
    return moment(startDate).isSame(this.targetMonth);
  }

  private isInActivityPeriod(date: string): boolean {
    const startDate = moment(new Date(this.campaignStart)).format('YYYY-MM-DD');
    const finishDate = moment(new Date(this.campaignFinish)).format('YYYY-MM-DD');
    return moment(date).isBetween(startDate, finishDate, undefined, '[]');
  }

  private initPeriodWorker(): void {
    this.targetMonth$.pipe()
      .subscribe((date) => this.targetMonth = moment(date).format('YYYY-MM'));
  }

  nextPeriod(): void {
    this.targetMonth$
      .pipe(
        take(1),
        filter(() => !this.isNextFinished)
      )
      .subscribe((date) => {
        const nextMonth = moment(date).add(1, 'month').format('YYYY-MM');
        this.targetMonth$.next(nextMonth);
      });
  }

  prevPeriod(): void {
    this.targetMonth$
      .pipe(
        take(1),
        filter(() => !this.isPrevFinished)
      )
      .subscribe((date) => {
        const prevMonth = moment(date).subtract(1, 'month').format('YYYY-MM');
        this.targetMonth$.next(prevMonth);
      });
  }

  get getActivity$(): Observable<IDeviceCalendarFullData> {
    return this.targetMonth$
      .pipe(
        switchMap((date) => of(this.getCalendarData(date)))
      );
  }

  private get targetMonthLabel(): { month: string, year: number } {
    return {
      month: moment(this.targetMonth$.value).format('MMMM'),
      year: moment(this.targetMonth$.value).year(),
    };
  }

  private get calendarColumnHeader(): ICalendarDayInfo[] {
    const daysInMonth = moment(this.targetMonth$.value).daysInMonth();
    const data = Array.from({length: daysInMonth}, (_, i) => {
      const day = i + 1;
      const dayDate = moment().set(
        {year: moment(this.targetMonth$.value).year(), month: moment(this.targetMonth$.value).month(), date: day}
      ).format('YYYY-MM-DD');

      return {
        dayNumber: day,
        date: dayDate,
        isActivityPeriod: this.isInActivityPeriod(dayDate)
      };
    });

    return data;
  }

  private getDayActivityStatus(activityStatus: boolean, targetDate: string, deviceTimezone: string): DeviceDayStatusEnum {
    const currentDay = moment.tz(deviceTimezone).format('YYYY-MM-DD');
    const isDatePassed = moment(currentDay).isAfter(targetDate);

    if (activityStatus) return DeviceDayStatusEnum.active;
    return isDatePassed ? DeviceDayStatusEnum.inactive : DeviceDayStatusEnum.dayOff;
  }

  private getDeviceTargetMonthActivity(
    {targetPeriod, periods, deviceTimezone}:
      {
        targetPeriod: string, periods: IDeviceDayInfo[], deviceTimezone: string
      }
  ): IDeviceMonthInfo[] {
    const monthActivity: IDeviceMonthInfo[] = [];

    const targetMonth = moment(targetPeriod).format('YYYY-MM');
    const daysInMonth = moment(targetMonth).daysInMonth();

    const getExistedMonthActivity = periods.filter((day) => {
      const month = moment.tz(day.start, deviceTimezone).format('YYYY-MM');
      return moment(month).isSame(targetMonth);
    });

    for (let i = 1; i <= daysInMonth; i++) {
      const day = i;
      const dayWithActivity = getExistedMonthActivity.find((item) => {
        return moment.tz(item.start, deviceTimezone).date() === day;
      });
      if (dayWithActivity) {
        const targetDate = moment.tz(dayWithActivity.start, deviceTimezone).format('YYYY-MM-DD');
        monthActivity.push({
          date: targetDate,
          days: {
            dayNumber: day,
            status: this.getDayActivityStatus(dayWithActivity.active, targetDate, deviceTimezone)
          }
        });
      } else {
        const momentDate = moment().tz(deviceTimezone)
          .set({year: moment(targetMonth).year(), month: moment(targetMonth).month(), date: day});
        monthActivity.push({
          date: momentDate.format('YYYY-MM-DD'),
          days: {
            dayNumber: day,
            status: DeviceDayStatusEnum.dayOff
          }
        });
      }
    }

    return monthActivity;
  }

  private getCalendarData(targetMonth: string): IDeviceCalendarFullData {
    const devices = this.devicesActivity.map((device) => {
      return {
        deviceName: device.deviceName,
        locationName: device.locationName,
        activity: this.getDeviceTargetMonthActivity({
          periods: device.periods,
          deviceTimezone: device.timezone,
          targetPeriod: targetMonth
        })
      };
    });

    return {
      devices,
      calendarHeader: this.calendarColumnHeader,
      monthLabel: this.targetMonthLabel,
      isNextDisabled: this.isNextFinished,
      isPrevDisabled: this.isPrevFinished,
      campaignStart: this.campaignStart,
      campaignFinish: this.campaignFinish
    };
  }
}
